/**
 * relevanceEngine.js — Waiting List V2
 *
 * Calculates opportunity relevance scores and provides human-friendly labels.
 */

/**
 * Calculates the relevance score for an opportunity.
 *
 * @param {Object} params
 * @param {number} params.dailyGoal        - Daily appointment goal
 * @param {number} params.scheduledCount   - Already scheduled for the day
 * @param {number} params.freeSlotsCount   - Free slots available that day
 * @param {number} params.waitingDays      - Days the patient has been waiting
 * @param {string} params.priority         - Patient priority (HIGH, MEDIUM, LOW)
 * @param {number} params.daysUntilSlot    - Days until the available slot
 * @param {boolean} params.preferredProfessional - Whether it matches preferred professional
 * @param {boolean} params.preferredTime   - Whether the slot time matches patient preference
 * @returns {number} Score from 0 to 100
 */
export function calculateRelevanceScore({
  dailyGoal = 8,
  scheduledCount = 0,
  freeSlotsCount = 1,
  waitingDays = 0,
  priority = 'MEDIUM',
  daysUntilSlot = 0,
  preferredProfessional = true,
  preferredTime = false,
}) {
  let score = 0;

  // === High weight (up to 60 points) ===

  // Distance from daily goal (0–30 points)
  const goalGap = Math.max(0, dailyGoal - scheduledCount);
  const goalRatio = dailyGoal > 0 ? goalGap / dailyGoal : 1;
  score += Math.round(goalRatio * 30);

  // Free slots urgency (0–15 points)
  const freeScore = Math.min(freeSlotsCount / Math.max(dailyGoal, 1), 1);
  score += Math.round(freeScore * 15);

  // Preferred professional match (0–15 points)
  score += preferredProfessional ? 15 : 5;

  // === Medium weight (up to 30 points) ===

  // Waiting time (0–20 points, capped at 30 days)
  const waitScore = Math.min(waitingDays / 30, 1);
  score += Math.round(waitScore * 20);

  // Priority (0–10 points)
  const priorityScores = { HIGH: 10, MEDIUM: 5, LOW: 2 };
  score += priorityScores[priority] || 5;

  // === Low weight (up to 10 points) ===

  // Slot proximity (0–7 points)
  const proximityScore = Math.max(0, 1 - daysUntilSlot / 30);
  score += Math.round(proximityScore * 7);

  // Preferred time match (0–3 points)
  score += preferredTime ? 3 : 0;

  return Math.min(100, Math.max(0, score));
}

/**
 * Returns a human-friendly compatibility label based on score.
 * Thresholds are calibrated to the realistic score range (30–80).
 *
 * @param {number} score
 * @returns {{ label: string, color: string }}
 */
export function getCompatibilityLabel(score) {
  if (score >= 75) return { label: 'Oportunidade ideal', color: '#059669' };
  if (score >= 60) return { label: 'Alta compatibilidade', color: '#2563eb' };
  if (score >= 45) return { label: 'Boa opção', color: '#d97706' };
  return { label: 'Baixa prioridade', color: '#94a3b8' };
}

/**
 * Returns an array of human-readable reasons why this slot is recommended.
 *
 * @param {Object} params
 * @param {boolean} params.preferredProfessional
 * @param {boolean} params.preferredTime
 * @param {number}  params.waitingDays
 * @param {string}  params.priority
 * @param {number}  params.daysUntilSlot
 * @param {number}  params.dailyGoal
 * @param {number}  params.scheduledCount
 * @returns {string[]}
 */
export function getScoreReasons({
  preferredProfessional = false,
  preferredTime = false,
  waitingDays = 0,
  priority = 'MEDIUM',
  daysUntilSlot = 0,
  dailyGoal = 8,
  scheduledCount = 0,
}) {
  const reasons = [];

  if (preferredProfessional) reasons.push('Profissional preferido');
  if (preferredTime) reasons.push('Horário dentro da preferência');
  if (priority === 'HIGH') reasons.push('Paciente de alta prioridade');
  if (waitingDays >= 14) reasons.push(`Aguardando há ${waitingDays} dias`);
  else if (waitingDays >= 7) reasons.push(`Aguardando há ${waitingDays} dias`);
  if (daysUntilSlot <= 3) reasons.push('Horário em breve');
  if (scheduledCount < dailyGoal) reasons.push('Dia abaixo da meta de atendimentos');

  // Always return at least one reason
  if (reasons.length === 0) reasons.push('Compatível com a disponibilidade');

  return reasons;
}

/**
 * Sorts opportunities by relevance score (highest first).
 */
export function sortByRelevance(opportunities) {
  return [...opportunities].sort((a, b) => (b.score || 0) - (a.score || 0));
}
